<template>
    <div class="clinical-exam-form">
      <!-- Back button and page title -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <router-link :to="returnPath" class="btn btn-outline-primary">
          <i class="bi bi-arrow-left me-2"></i>Volver
        </router-link>
        <h1>{{ isEditMode ? 'Editar Examen Clínico' : 'Nuevo Examen Clínico' }}</h1>
      </div>
  
      <!-- Loading state -->
      <div v-if="loading" class="d-flex justify-content-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
  
      <!-- Form content -->
      <div v-else class="row">
        <div class="col-md-10 mx-auto">
          <div class="card">
            <div class="card-body">
              <form @submit.prevent="submitForm">
                <!-- Patient information (read-only) -->
                <div class="mb-4">
                  <h5 class="card-title">Información del Paciente</h5>
                  <div v-if="patient" class="d-flex align-items-center mb-3">
                    <div class="avatar-placeholder me-3">
                      {{ getInitials(patient.first_name, patient.last_name) }}
                    </div>
                    <div>
                      <h6 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h6>
                      <p v-if="patient.fecha_nacimiento" class="text-muted mb-0">
                        {{ formatAge(patient.fecha_nacimiento) }} años
                      </p>
                    </div>
                  </div>
                </div>
  
                <hr class="my-4">
  
                <!-- Clinical Exam Form Fields -->
                <div class="row g-3">
                  <!-- Profile -->
                  <div class="col-md-6">
                    <label class="form-label">Perfil</label>
                    <select 
                      class="form-select" 
                      v-model="form.profile"
                    >
                      <option value="">Seleccionar</option>
                      <option value="concavo">Cóncavo</option>
                      <option value="convexo">Convexo</option>
                      <option value="recto">Recto</option>
                    </select>
                  </div>
  
                  <!-- Lip Closure -->
                  <div class="col-md-6">
                    <label class="form-label">Cierre Labial</label>
                    <select 
                      class="form-select" 
                      v-model="form.lip_closure"
                    >
                      <option value="">Seleccionar</option>
                      <option value="normal">Normal</option>
                      <option value="forzado">Forzado</option>
                      <option value="incompetencia">Incompetencia Labial</option>
                    </select>
                  </div>
  
                  <!-- Frontal Analysis -->
                  <div class="col-md-6">
                    <label class="form-label">Análisis Frontal</label>
                    <select 
                      class="form-select" 
                      v-model="form.frontal_analysis"
                    >
                      <option value="">Seleccionar</option>
                      <option value="simetrico">Simétrico</option>
                      <option value="asimetrico_derecho">Asimétrico Lado Derecho</option>
                      <option value="asimetrico_izquierdo">Asimétrico Lado Izquierdo</option>
                    </select>
                  </div>
  
                  <!-- Smile Height -->
                  <div class="col-md-6">
                    <label class="form-label">Altura de Sonrisa</label>
                    <select 
                      class="form-select" 
                      v-model="form.smile_height"
                    >
                      <option value="">Seleccionar</option>
                      <option value="baja">Baja</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                    </select>
                  </div>
  
                  <!-- Upper Midline -->
                  <div class="col-md-6">
                    <label class="form-label">Línea Media Superior</label>
                    <select 
                      class="form-select" 
                      v-model="form.upper_midline"
                    >
                      <option value="">Seleccionar</option>
                      <option value="coincidente">Coincidente con Línea Media Facial</option>
                      <option value="desviada_derecha">Desviada a la Derecha</option>
                      <option value="desviada_izquierda">Desviada a la Izquierda</option>
                    </select>
                  </div>
  
                  <!-- Lower Midline -->
                  <div class="col-md-6">
                    <label class="form-label">Línea Media Inferior</label>
                    <select 
                      class="form-select" 
                      v-model="form.lower_midline"
                    >
                      <option value="">Seleccionar</option>
                      <option value="coincidente">Coincidente con Línea Media Facial</option>
                      <option value="desviada_derecha">Desviada a la Derecha</option>
                      <option value="desviada_izquierda">Desviada a la Izquierda</option>
                    </select>
                  </div>
  
                  <!-- Maxillary Cant -->
                  <div class="col-md-6">
                    <label for="maxillaryCant" class="form-label">Canteo Maxilar (mm)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      class="form-control" 
                      id="maxillaryCant"
                      v-model="form.maxillary_cant"
                    >
                  </div>
  
                  <!-- Canine and Molar Classes -->
                  <div class="col-md-6">
                    <label class="form-label">Clase Canina Derecha</label>
                    <select 
                      class="form-select" 
                      v-model="form.right_canine_class"
                    >
                      <option value="">Seleccionar</option>
                      <option value="I">Clase I</option>
                      <option value="II">Clase II</option>
                      <option value="III">Clase III</option>
                      <option value="N/V">No Valorable</option>
                    </select>
                  </div>
  
                  <div class="col-md-6">
                    <label class="form-label">Clase Molar Derecha</label>
                    <select 
                      class="form-select" 
                      v-model="form.right_molar_class"
                    >
                      <option value="">Seleccionar</option>
                      <option value="I">Clase I</option>
                      <option value="II">Clase II</option>
                      <option value="III">Clase III</option>
                      <option value="N/V">No Valorable</option>
                    </select>
                  </div>
  
                  <div class="col-md-6">
                    <label class="form-label">Clase Canina Izquierda</label>
                    <select 
                      class="form-select" 
                      v-model="form.left_canine_class"
                    >
                      <option value="">Seleccionar</option>
                      <option value="I">Clase I</option>
                      <option value="II">Clase II</option>
                      <option value="III">Clase III</option>
                      <option value="N/V">No Valorable</option>
                    </select>
                  </div>
  
                  <div class="col-md-6">
                    <label class="form-label">Clase Molar Izquierda</label>
                    <select 
                      class="form-select" 
                      v-model="form.left_molar_class"
                    >
                      <option value="">Seleccionar</option>
                      <option value="I">Clase I</option>
                      <option value="II">Clase II</option>
                      <option value="III">Clase III</option>
                      <option value="N/V">No Valorable</option>
                    </select>
                  </div>
  
                  <!-- Overjet and Overbite -->
                  <div class="col-md-6">
                    <label for="overjet" class="form-label">Overjet (mm)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      class="form-control" 
                      id="overjet"
                      v-model="form.overjet"
                    >
                  </div>
  
                  <div class="col-md-6">
                    <label for="overbite" class="form-label">Overbite (mm)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      class="form-control" 
                      id="overbite"
                      v-model="form.overbite"
                    >
                  </div>
  
                  <!-- Crossbite and Open Bite -->
                  <div class="col-md-6">
                    <label class="form-label">Mordida Cruzada Anterior</label>
                    <select 
                      class="form-select" 
                      v-model="form.anterior_crossbite"
                    >
                      <option value="">Seleccionar</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
  
                  <div class="col-md-6">
                    <label class="form-label">Mordida Cruzada Posterior</label>
                    <select 
                      class="form-select" 
                      v-model="form.posterior_crossbite"
                    >
                      <option value="">Seleccionar</option>
                      <option value="no">No</option>
                      <option value="derecha">Derecha</option>
                      <option value="izquierda">Izquierda</option>
                    </select>
                  </div>
  
                  <div class="col-md-6">
                    <label class="form-label">Mordida Abierta Anterior</label>
                    <select 
                      class="form-select" 
                      v-model="form.anterior_open_bite"
                    >
                      <option value="">Seleccionar</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
  
                  <div class="col-md-6">
                    <label class="form-label">Mordida Abierta Posterior</label>
                    <select 
                      class="form-select" 
                      v-model="form.posterior_open_bite"
                    >
                      <option value="">Seleccionar</option>
                      <option value="no">No</option>
                      <option value="derecha">Derecha</option>
                      <option value="izquierda">Izquierda</option>
                    </select>
                  </div>
  
                  <div class="col-md-6">
                    <label class="form-label">Mordida Profunda</label>
                    <select 
                      class="form-select" 
                      v-model="form.deep_bite"
                    >
                      <option value="">Seleccionar</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </div>
  
                  <!-- Interposition -->
                  <div class="col-md-6">
                    <label class="form-label">Interposición</label>
                    <select 
                      class="form-select" 
                      v-model="form.interposition"
                    >
                      <option value="">Seleccionar</option>
                      <option value="no_presenta">No Presenta</option>
                      <option value="labial">Labial</option>
                      <option value="lingual">Lingual</option>
                    </select>
                  </div>
  
                  <!-- TMJ Observations -->
                  <div class="col-12">
                    <label for="tmjObservations" class="form-label">ATM (Articulación Temporomandibular)</label>
                    <textarea
                      id="tmjObservations"
                      class="form-control"
                      v-model="form.tmj_observations"
                      rows="3"
                      placeholder="Ruidos, saltos, hipomovilidad, hipermovilidad, laterognatia, etc."
                    ></textarea>
                  </div>
  
                  <!-- Form actions -->
                  <div class="col-12 mt-4">
                    <div class="d-flex justify-content-end">
                      <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm">
                        Cancelar
                      </button>
                      <button type="submit" class="btn btn-primary" :disabled="submitting || !isFormValid">
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ isEditMode ? 'Guardar cambios' : 'Guardar examen clínico' }}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { clinicalExamService, patientService } from '@/services/api';
  
  export default {
    name: 'ClinicalExamForm',
    data() {
      return {
        form: {
          clinical_history: null,
          profile: '',
          lip_closure: '',
          frontal_analysis: '',
          smile_height: '',
          upper_midline: '',
          lower_midline: '',
          maxillary_cant: null,
          right_canine_class: '',
          right_molar_class: '',
          left_canine_class: '',
          left_molar_class: '',
          overjet: null,
          overbite: null,
          anterior_crossbite: '',
          posterior_crossbite: '',
          anterior_open_bite: '',
          posterior_open_bite: '',
          deep_bite: '',
          interposition: '',
          tmj_observations: ''
        },
        originalForm: {},
        patient: null,
        loading: true,
        submitting: false,
        error: null
      };
    },
    computed: {
      isEditMode() {
        return this.$route.path.includes('/edit');
      },
      clinicalExamId() {
        return this.$route.params.id;
      },
      returnPath() {
        if (this.isEditMode) {
          return `/clinical-exam/${this.clinicalExamId}`;
        } else if (this.patient) {
          return `/patients/${this.patient.id}`;
        } else {
          return '/clinical-histories';
        }
      },
      isFormValid() {
        // In edit mode, we always allow saving
        if (this.isEditMode) return true;
        
        // In create mode, we need a clinical history
        return this.form.clinical_history !== null;
      }
    },
    async created() {
    try {
      if (this.isEditMode) {
        await this.fetchClinicalExam();
      } else {
        // Check if a clinical history ID was provided in the query params
        const clinicalHistoryId = this.$route.query.clinical_history;
        if (clinicalHistoryId) {
          this.form.clinical_history = parseInt(clinicalHistoryId);
          await this.fetchPatientFromClinicalHistory(clinicalHistoryId);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'No se pudieron cargar los datos necesarios. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchClinicalExam() {
      const response = await clinicalExamService.get(this.clinicalExamId);
      this.form = { ...response.data };
      this.originalForm = { ...response.data };
      
      // Fetch patient data from clinical history
      if (this.form.clinical_history) {
        await this.fetchPatientFromClinicalHistory(this.form.clinical_history);
      }
    },
    
    async fetchPatientFromClinicalHistory(clinicalHistoryId) {
      // Assuming you have an API method to get patient from clinical history
      const response = await clinicalHistoryService.getPatient(clinicalHistoryId);
      this.patient = response.data;
    },
    
    async submitForm() {
      this.submitting = true;
      this.error = null;

      try {
        if (this.isEditMode) {
          await clinicalExamService.update(this.clinicalExamId, this.form);
          this.$router.push(`/clinical-exam/${this.clinicalExamId}`);
        } else {
          const response = await clinicalExamService.create(this.form);
          this.$router.push(`/clinical-exam/${response.data.id}`);
        }
      } catch (error) {
        console.error('Error saving clinical exam:', error);
        this.error = 'Error al guardar el examen clínico. Por favor, intenta de nuevo.';
      } finally {
        this.submitting = false;
      }
    },
    
    resetForm() {
      if (this.isEditMode) {
        // In edit mode, reset to original values
        this.form = { ...this.originalForm };
      } else {
        // In create mode, clear the form but keep the clinical history
        const clinicalHistoryId = this.form.clinical_history;
        this.form = {
          clinical_history: clinicalHistoryId,
          profile: '',
          lip_closure: '',
          frontal_analysis: '',
          smile_height: '',
          upper_midline: '',
          lower_midline: '',
          maxillary_cant: null,
          right_canine_class: '',
          right_molar_class: '',
          left_canine_class: '',
          left_molar_class: '',
          overjet: null,
          overbite: null,
          anterior_crossbite: '',
          posterior_crossbite: '',
          anterior_open_bite: '',
          posterior_open_bite: '',
          deep_bite: '',
          interposition: '',
          tmj_observations: ''
        };
      }
      
      this.error = null;
    },
    
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    
    formatAge(dateString) {
      if (!dateString) return '';
      const birthDate = new Date(dateString);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age;
    }
  }
};
</script>

<style scoped>
.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #495057;
}
</style>